import React, { useEffect, useState } from 'react'
import { useNavigate, } from 'react-router-dom'
import { CheckCircle2, Download } from 'lucide-react'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { showToastNotification } from '@/utility/helper'
import { ThrowError } from '@/api/callApi'
import { RoutesLink } from '@/utility/RouteLinks'
import FormField from '@/components/common/FormField'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { IOffboardingDetails, IOffboardingProcessStep } from '@/interface/offboarding.interface'

interface SettlementStepProps {
    isLoading: boolean;
    setIsLoading: (val: boolean) => void;
    userId: string;
    noDuesUserId?: string;
    clearanceSteps: IOffboardingProcessStep[];
    isCompleted: boolean;
    setIsCompleted: (val: boolean) => void;
    initialSystemAccessAvailableAt?: string;
    initialStatus?: string;
    initialData?: IOffboardingDetails | null;
}

interface ISettlementForm {
    systemAccessAvailableAt: string;
    status: string;
}

const SettlementStep: React.FC<SettlementStepProps> = ({
    isLoading,
    setIsLoading,
    userId,
    clearanceSteps,
    isCompleted,
    noDuesUserId,
    setIsCompleted,
    initialData,
    initialSystemAccessAvailableAt = "",
    initialStatus = "InProgress"
}) => {

    const navigate = useNavigate()
    const [isDownloading, setIsDownloading] = useState(false)


    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<ISettlementForm>({
        defaultValues: {
            systemAccessAvailableAt: "",
            status: "InProgress"
        }
    })

    const systemAccessAvailableDate = watch("systemAccessAvailableAt");

    const minDateString = initialData?.lastWorkingDate
        ? moment(initialData.lastWorkingDate).add(1, 'days').format('YYYY-MM-DD')
        : undefined;


    useEffect(() => {
        reset({
            systemAccessAvailableAt: initialSystemAccessAvailableAt
                ? moment(initialSystemAccessAvailableAt, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601]).format("YYYY-MM-DD")
                : "",
            status: initialStatus || "InProgress"
        })
    }, [initialSystemAccessAvailableAt, initialStatus, reset])

    const onSubmit = async (data: ISettlementForm) => {
        if (!userId) return
        if (!data.systemAccessAvailableAt) {
            showToastNotification("danger", "System Access Available date is required to complete offboarding")
            return
        }
        setIsLoading(true)
        try {
            // Update or add the ff-settlement step in offboardingProcess safely without duplicates
            const hasFF = clearanceSteps.some(x => x.key === "ff-settlement")
            const updatedProcess = hasFF
                ? clearanceSteps.map(x => x.key === "ff-settlement" ? { ...x, status: "COMPLETED" as const } : x)
                : [...clearanceSteps, { key: "ff-settlement", title: "Full & Final Settlement", status: "COMPLETED" as const }]

            const payload = {
                offboardingProcess: updatedProcess,
                systemAccessAvailableAt: data.systemAccessAvailableAt
                    ? moment(data.systemAccessAvailableAt, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601]).format("YYYY-MM-DD")
                    : null,
                status: "Completed"
            }
            const res = await OffboardingService.updateOffboarding(userId, payload)
            if (res?.status) {
                showToastNotification("success", "Employee offboarding completed and settled!")
                setIsCompleted(true)
                navigate(RoutesLink.offboardingList)
            }
        } catch (err) {
            ThrowError(err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDownloadNoDues = async () => {
        if (!noDuesUserId) {
            showToastNotification("danger", "User ID not found");
            return;
        }
        setIsDownloading(true);
        try {
            const rawResponse = await OffboardingService.downloadNoDuesReport(noDuesUserId);
            const pdfBlob = new Blob([rawResponse], { type: "application/pdf" });

            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `no_dues_form_${noDuesUserId}.pdf`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToastNotification("success", "No Dues Form downloaded successfully!");
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsDownloading(false);
        }
    };

    const hasClearanceRemaining = clearanceSteps
        .filter(x => x.key !== "ff-settlement")
        .some(x => x.status !== "COMPLETED")

    useEffect(() => {
        if (initialData?.lastWorkingDate && systemAccessAvailableDate) {
            const lastWorkingMoment = moment(initialData.lastWorkingDate);
            const systemAccessMoment = moment(systemAccessAvailableDate, ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601]);
            if (systemAccessMoment.isSameOrBefore(lastWorkingMoment, 'day')) {
                const nextDay = lastWorkingMoment.add(1, 'days').format("YYYY-MM-DD");
                setValue("systemAccessAvailableAt", nextDay);
            }
        }
    }, [systemAccessAvailableDate, initialData?.lastWorkingDate, setValue]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Input Forms Card */}
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-6 space-y-6">
                <h4 className="font-bold text-slate-800">Exit Status and Access Config</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                        type="date"
                        label="System Access Available At"
                        disabled={isCompleted}
                        min={minDateString}
                        {...register("systemAccessAvailableAt", {
                            required: "System access available date is required",
                            validate: (val) => {
                                if (!val) return "System access available date is required";
                                if (minDateString && val < minDateString) {
                                    return `System access date must be after Last Working Day (${minDateString})`;
                                }
                                return true;
                            }
                        })}
                        error={errors.systemAccessAvailableAt?.message}
                        required
                    />
                    {/* <FormField
                        type="select"
                        label="Offboarding Status"
                        disabled={isCompleted}
                        {...register("status")}
                        value={watch("status") ?? ""}
                        options={[
                            { value: "Initiated", label: "Initiated" },
                            { value: "InProgress", label: "In Progress" },
                            { value: "Completed", label: "Completed" }
                        ]}
                    /> */}
                </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 text-center space-y-4 max-w-md mx-auto">
                <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                    <CheckCircle2 size={32} />
                </div>
                <h4 className="font-bold text-slate-800 text-lg">Full & Final Settlement</h4>
                <p className="text-sm text-slate-500 leading-relaxed">
                    Ensure clearance approvals from all departments (Asset and Account Clearance) are fully completed before final completion.
                </p>
                {isCompleted ? (
                    <div className="space-y-3 pt-2">
                        <div className="inline-block py-1.5 px-3 bg-green-100 border border-green-200 rounded-full text-green-700 text-xs font-bold">
                            Settlement Completed
                        </div>
                        <div>
                            <Button
                                type="button"
                                onClick={handleDownloadNoDues}
                                disabled={isDownloading}
                                className="w-full rounded-xl py-3 text-sm font-bold bg-primary hover:bg-primary/90 text-white shadow-md flex items-center justify-center gap-2"
                            >
                                {isDownloading ? (
                                    <>
                                        <Spinner className="w-4 h-4 mr-2" /> Downloading...
                                    </>
                                ) : (
                                    <>
                                        <Download size={16} /> Download No Dues Form
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="pt-4">
                        <Button
                            type="submit"
                            disabled={isLoading || hasClearanceRemaining}
                            className="w-full rounded-xl py-3 text-sm font-bold bg-green-600 hover:bg-green-700 text-white shadow-md disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none"
                        >
                            {isLoading ? <Spinner /> : 'Settle & Close Employee Account'}
                        </Button>
                        {hasClearanceRemaining && (
                            <p className="text-xs text-amber-600 mt-2 font-medium">
                                ⚠️ Fill and complete previous clearance tabs to settle.
                            </p>
                        )}
                    </div>
                )}
            </div>
        </form>
    )
}

export default SettlementStep
